/*
 * gifted. — magnetic clasp caution
 * PATH: assets/gifted-magnet-caution.js
 * v2 — 2026-08-30. Companion to blocks/gifted-magnet-caution.liquid v4.
 *
 * ── WHY v1 DID NOTHING, EVERY TIME, SILENTLY ────────────────────────────────
 *
 *   v1 read the re-rendered document from `result.html`. Measured on the live
 *   page 30 Aug 2026, shopify:product:select resolves with:
 *
 *       { variant: { id, title, availableForSale, price, selectedOptions },
 *         detail:  { html, productId, newProduct, sourceId, resource } }
 *
 *   The document is at result.detail.html. `result.html` is undefined, so v1
 *   hit its own guard and returned — no error, no console warning, nothing.
 *   The caution froze at whatever the server rendered on page load.
 *
 * ── WHAT THIS FILE DOES INSTEAD ─────────────────────────────────────────────
 *
 *   It does not read that document at all. variant.selectedOptions is a plain
 *   [{name, value}] array on the event's own payload — no parsing, no network,
 *   no undocumented shape to depend on. The script toggles ONE attribute.
 *
 *   Liquid still sets the state the page loads with, including `hidden`.
 *
 * ── FAIL-OPEN IS THE DEFAULT IN EVERY BRANCH ────────────────────────────────
 *
 *   Promise rejects · payload missing · option not found · value unreadable ·
 *   exception thrown · this file never loads  →  THE CAUTION IS SHOWN.
 *
 *   A caution shown when it need not be is noise. A caution hidden when it is
 *   needed is the failure the block exists to prevent. Never invert this.
 *
 * ── CUSTOM ELEMENT, NOT A ONE-SHOT LISTENER ─────────────────────────────────
 *   Horizon morphs sections. connectedCallback re-runs after a morph; a
 *   DOMContentLoaded handler would be silently lost.
 */
 
if (!customElements.get('gifted-magnet-caution')) {
  class GiftedMagnetCaution extends HTMLElement {
    #controller = null;
 
    connectedCallback() {
      this.#controller = new AbortController();
      const target = this.closest('.shopify-section, dialog');
      target?.addEventListener('shopify:product:select', this.#handleSelect, {
        signal: this.#controller.signal,
      });
    }
 
    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = null;
    }
 
    get #body() {
      return this.querySelector('[data-gifted-caution-body]');
    }
 
    #reveal() {
      const b = this.#body;
      if (b) b.hidden = false;
    }
 
    #handleSelect = async (event) => {
      const body = this.#body;
      if (!body) return;
 
      // No gate configured: Liquid renders it on every variant. Nothing to do.
      const optName = this.dataset.claspOption;
      const magnetValue = this.dataset.magnetValue;
      if (!optName || !magnetValue) return;
 
      let result;
      try {
        result = await event.promise;
      } catch {
        // AbortError on a superseded selection, or a failed fetch.
        // Do NOT reveal here — a superseded selection is immediately followed
        // by the one that supersedes it, which will set the state correctly.
        return;
      }
 
      try {
        const opts = result?.variant?.selectedOptions;
        if (!Array.isArray(opts) || opts.length === 0) {
          this.#reveal();
          return;
        }
 
        const key = optName.trim().toLowerCase();
        const match = opts.find(
          (o) => String(o?.name ?? '').trim().toLowerCase() === key
        );
 
        if (!match) {
          // The configured option is not among the selected options.
          this.#reveal();
          return;
        }
 
        const value = String(match.value ?? '').trim().toLowerCase();
        if (!value) {
          this.#reveal();
          return;
        }
 
        body.hidden = value !== magnetValue.trim().toLowerCase();
      } catch {
        this.#reveal();
      }
    };
  }
 
  customElements.define('gifted-magnet-caution', GiftedMagnetCaution);
}